import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { Modal } from '../components/Modal.tsx';
import type { SubmissionFile } from '../lib/types.ts';

// Preview an uploaded document in a modal: images inline, PDFs in an iframe, and
// a download/open fallback for anything the browser can't render inline. Pages
// through `files` when a question has more than one.
export function FilePreviewModal({
  companyId,
  submissionId,
  files,
  startIndex = 0,
  onClose,
}: {
  companyId: string;
  submissionId: string;
  files: SubmissionFile[];
  startIndex?: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startIndex);
  const file = files[index]!;
  const downloadPath = `/api/companies/${companyId}/submissions/${submissionId}/files/${file.id}/download-url`;

  const urlQuery = useQuery({
    queryKey: ['submission-file-url', submissionId, file.id],
    queryFn: () => adminApi.get<{ url: string }>(downloadPath),
    staleTime: 4 * 60 * 1000,
  });
  const url = urlQuery.data?.url;
  const isImage = file.mime_type.startsWith('image/');
  const isPdf = file.mime_type === 'application/pdf';

  function openInNewTab() {
    if (url) window.open(url, '_blank', 'noopener');
  }

  return (
    <Modal
      open
      title={file.file_name}
      size="lg"
      onClose={onClose}
      footer={
        <>
          {files.length > 1 && (
            <div className="mr-auto flex items-center gap-2 text-xs text-slate-500">
              <Btn size="sm" variant="ghost" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>
                Prev
              </Btn>
              <span>
                {index + 1} / {files.length}
              </span>
              <Btn
                size="sm"
                variant="ghost"
                disabled={index === files.length - 1}
                onClick={() => setIndex((i) => i + 1)}
              >
                Next
              </Btn>
            </div>
          )}
          <Btn size="sm" variant="ghost" disabled={!url} onClick={openInNewTab}>
            Open in new tab
          </Btn>
          <Btn size="sm" variant="secondary" onClick={onClose}>
            Close
          </Btn>
        </>
      }
    >
      {!url ? (
        <div className="flex h-40 items-center justify-center text-sm text-slate-400">
          {urlQuery.isError ? 'File unavailable' : 'Loading…'}
        </div>
      ) : isImage ? (
        <img src={url} alt={file.file_name} className="mx-auto max-h-[65vh] max-w-full rounded-md" />
      ) : isPdf ? (
        <iframe
          src={url}
          title={file.file_name}
          className="h-[65vh] w-full rounded-md border border-slate-200"
        />
      ) : (
        <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-500">
          <span>Preview not available for this file type.</span>
          <Btn size="sm" variant="secondary" onClick={openInNewTab}>
            Open in new tab
          </Btn>
        </div>
      )}
    </Modal>
  );
}
